import { Skeleton } from '@affine/component';
import {
  StorageUsageBar,
  storageUsageColor,
} from '@affine/core/components/storage-usage-bar';
import {
  type PrivateFolderStorageSnapshot,
  usePrivateFolderStorage,
} from '@affine/core/modules/organize';
import {
  fetchMyPrivateFolderStorage,
  type PrivateFolderStorageUsage,
  WorkspaceQuotaService,
} from '@affine/core/modules/quota';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import bytes from 'bytes';
import { useEffect, useState } from 'react';

import * as styles from './index.css';

type PrivateStorageUsageForDisplay =
  | Pick<PrivateFolderStorageUsage, 'limitBytes' | 'usedBytes'>
  | PrivateFolderStorageSnapshot
  | null;

const privateStorageToUsage = (usage: PrivateStorageUsageForDisplay) => {
  if (!usage?.limitBytes) {
    return {
      usedFormatted: null,
      maxFormatted: null,
      percent: null,
      color: null,
    };
  }
  const percent = Math.min(
    100,
    Math.max(
      0.5,
      Number(((usage.usedBytes / usage.limitBytes) * 100).toFixed(4))
    )
  );
  return {
    usedFormatted: bytes.format(usage.usedBytes),
    maxFormatted: bytes.format(usage.limitBytes),
    percent,
    color: storageUsageColor(percent),
  };
};

export const CloudUsage = () => {
  const t = useI18n();
  const quota = useService(WorkspaceQuotaService).quota;
  const workspaceId = useService(WorkspaceService).workspace.id;
  const quotaError = useLiveData(quota.error$);
  const cachedPrivateStorage = usePrivateFolderStorage();
  const [privateStorage, setPrivateStorage] =
    useState<PrivateFolderStorageUsage | null>(null);

  useEffect(() => {
    // revalidate quota to get the latest status
    quota.revalidate();
  }, [quota]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchMyPrivateFolderStorage(workspaceId, abortController.signal)
      .then(setPrivateStorage)
      .catch(() => {
        if (!abortController.signal.aborted) {
          setPrivateStorage(null);
        }
      });
    return () => abortController.abort();
  }, [workspaceId]);

  const color = useLiveData(quota.color$);
  const usedFormatted = useLiveData(quota.usedFormatted$);
  const maxFormatted = useLiveData(quota.maxFormatted$);
  const percent = useLiveData(quota.percent$);
  const privateUsage = privateStorageToUsage(
    privateStorage ?? cachedPrivateStorage
  );

  if (percent === null) {
    if (quotaError) {
      // Gracefully hide instead of showing error — quota may fail
      // when offline or backend returns non-32bit integers
      return null;
    }
    return (
      <div>
        <Skeleton height={15} width={50} />
        <Skeleton height={10} style={{ marginTop: 4 }} />
      </div>
    );
  }

  return (
    <>
      <StorageUsageBar
        className={styles.cloudUsageBlock}
        label={t['com.streamlain.workspace.storage']()}
        labelColor={cssVarV2('text/primary')}
        usedText={usedFormatted ?? ''}
        limitText={maxFormatted ?? ''}
        percent={percent}
        color={color}
        trackHeight={10}
      />
      <StorageUsageBar
        className={styles.cloudUsageBlock}
        label={t['com.streamlain.settings.account.privateFolder.storage']()}
        labelColor={cssVarV2('text/primary')}
        usedText={privateUsage.usedFormatted ?? ''}
        limitText={privateUsage.maxFormatted ?? ''}
        percent={privateUsage.percent}
        color={privateUsage.color}
        trackHeight={10}
      />
    </>
  );
};
